/**
 * The console's design tokens — the SINGLE source for every colour, font and
 * tone in `webapp/src/client`. No other file under `src/client` may contain a
 * hex literal; `tokens.test.ts` enforces that by scanning the directory.
 *
 * Values are copied VERBATIM from the source design
 * (`LedAI Webapp v2.html`, the `const C = {...}` block at the top of its
 * script) which is itself `brand-palette.md`'s light theme, settled
 * 2026-05-27. See docs/superpowers/specs/2026-08-25-webapp-console-design.md
 * §6.2 — that section names eight of these by hand (ink, surface, gold,
 * green, amber, red, blue, canvas); the rest are the same palette's
 * supporting tones, kept here rather than re-derived so the console and the
 * design cannot drift.
 *
 * `canvas` is the page ground behind the app frame. It is the one token the
 * spec names that the design's `C` object does not carry — the design writes
 * it inline as the root container's `background:#FBF4E8`.
 */

export const C = {
  ink: "#14110C",
  sub: "#6E6357",
  sub2: "#9C9180",
  surface: "#FFFDF8",
  faint: "#F1E9D8",
  sunk: "#F3ECDB",
  /** Bar track behind a proportional fill (adoption seat bars, seat split).
   *  Taken from the source design's own bar treatment, not invented. */
  track: "#EFE7D8",
  /** A bar with nothing to show — a seat signed in but with zero walks.
   *  Distinct from `track`: the track is background, this is a real but empty
   *  measurement. */
  barEmpty: "#DCD4C2",
  /** The initials chip for a seat that has NEVER been used. Grey rather than
   *  ink so a dormant row reads as absent at a glance, which is the one thing
   *  the adoption roster exists to show. */
  dormant: "#B9B0A0",
  gold: "#F4BE2E",
  /** Gold as TEXT on the paper ground — the design's link/control gold;
   *  `gold` itself is the bright accent and fails contrast as small text. */
  goldInk: "#B08A1E",

  /** The photo viewer's dark room: its ground and the meta rail beside the
   *  photo. Nothing else in the console is dark; these are the viewer's. */
  viewerBg: "#111110",
  viewerRail: "#1B1A18",
  onGold: "#1F1A10",
  green: "#00B877",
  greenSoft: "#DFF3EA",
  amber: "#C48A00",
  amberSoft: "#F7EBCF",
  /** A due date inside the week (today, tomorrow, this Friday). Linear's
   *  orange, not `amber`: amber is this console's "pending" gold and a date
   *  drawn in it read as a status rather than as a nudge. */
  orange: "#E8853D",
  red: "#D94B3B",
  redSoft: "#FBE3DC",
  /** `red` as a HAIRLINE on its own soft ground — the item record's stall
   *  banner (design: "Led AI - Item Record Card"). The full stroke drew a box
   *  that shouted as loudly as the sentence inside it. */
  redLine: "rgba(209,74,50,0.18)",
  blue: "#0077C4",
  blueSoft: "#E1EFF8",
  line: "rgba(20,17,12,0.10)",
  /**
   * The "soft instrument" hairline — 7% rather than 10%.
   *
   * The console redesign's own note: *hairlines at 7–8%*. `line` is right on
   * a card that has to separate itself from the page; on the sidebar's
   * chrome, on a nav row's own edge, and on a stat pill, 10% draws a box
   * where the design draws an edge. Both exist because the difference is the
   * whole point of the redesign (design: "Led AI Console Redesign", 3a).
   */
  hair: "rgba(10,10,10,0.07)",
  /**
   * The sidebar's ground, and the warm gold atmosphere over it.
   *
   * A radial wash, not a fill: the design puts a 14%-gold bloom in the top
   * corner and an 8% one at the bottom, so the rail reads as lit rather than
   * as tinted. The nav rows sitting on it are near-white surfaces, which is
   * what makes the selected one legible WITHOUT a brand fill.
   *
   * All three values are verbatim from `Led AI Console.dc.html` and are
   * confirmed against the file itself, not against a screenshot of it: a
   * SCREENSHOT of the design reads the blooms back at 12% and 7%, and its
   * nav badge (`rgba(247,197,43,.55)` in the file) at 49%, because a P3
   * capture compresses saturated gold by ~12% while leaving the neutral
   * ground and the near-white rows exact. Sample gold off a render, never
   * off a capture.
   */
  railGround: "#F2EDE3",
  railGlow: "rgba(247,197,43,0.14)",
  railGlowLow: "rgba(247,197,43,0.08)",
  /** A nav row's own surface — near-white over the rail, not opaque. The
   *  selected one is the strong end of the same idea. */
  railRow: "rgba(255,255,255,0.55)",
  railRowSelected: "rgba(255,255,255,0.92)",
  /**
   * The rail's own ink, as ALPHA over the lit ground rather than as the
   * page's opaque greys.
   *
   * The rail is the one surface in this console that is neither white nor
   * the page: `sub` (#6E6357) and `sub2` were mixed for a warm white card,
   * and on the gold-washed ground they read as brown text rather than as
   * quiet text. The design sets every rail label as `rgba(10,10,10,α)` so
   * each one picks up the wash beneath it — verbatim from the four alphas
   * `Led AI Console.dc.html` uses on the aside, which is why they are four
   * tokens and not one ramp.
   */
  railInk: "rgba(10,10,10,0.84)",
  /** A resting row's label. */
  railText: "rgba(10,10,10,0.72)",
  /** A resting row's icon, and the workspace sub-line. */
  railSoft: "rgba(10,10,10,0.45)",
  /** The workspace row's chevron — half ink. The design gives this one glyph
   *  its own value: it is the only affordance in the rail's head, and at the
   *  icons' 45% it stopped reading as a control. */
  railGlyph: "rgba(10,10,10,0.5)",
  /** The group label and a row's count — the rail's quietest ink. */
  railMeta: "rgba(10,10,10,0.38)",
  /** The chosen row in a rail menu — 4% ink, the design's own value. It is a
   *  hint that the check beside it confirms, not a selection on its own. */
  railMenuOn: "rgba(10,10,10,0.04)",
  /** The chrome of the icon rail's search circle (and Ask's history
   *  control). 8%: a control has to look pressable where a nav row does not. */
  railControl: "rgba(10,10,10,0.08)",
  /** The project pill's label while it says "All projects" — a scope nobody
   *  chose, printed a step quieter than a project somebody did. */
  railScope: "rgba(10,10,10,0.62)",
  /** The rail's right edge and the live pill's — 6%, a step under `hair`.
   *  The rail already separates itself by being lit; at 7% the edge became
   *  the loudest line on the screen. */
  railEdge: "rgba(10,10,10,0.06)",
  /** Review's count badge: gold at 55%, the one brand fill in the rail. It
   *  is a COUNT, not a destination, which is why the row it sits in is still
   *  a white surface when selected rather than a gold one. */
  railBadge: "rgba(247,197,43,0.55)",
  /**
   * The inset inside a WHITE card — a field table, a card's action footer.
   *
   * Barely off-white on purpose: `canvas` is the page's own warm ground and
   * reads as a hole punched in the card, where this reads as a recess in it.
   * The redesign uses it for every nested surface (design: "Led AI Console").
   */
  inset: "#FBFAF7",
  /** The rule BETWEEN rows inside a card — lighter still than `hair`, which
   *  is the card's own edge. Twenty rows separated at 7% read as a grid; at
   *  5% they read as a list. */
  hairRow: "rgba(10,10,10,0.05)",
  /** The redesign's two-layer shadow, which is how a surface reads as raised
   *  without a border doing the work. Never one hard shadow: the near layer
   *  is the edge, the far one is the lift. */
  softShadow: "rgba(10,10,10,0.04)",
  softShadowLow: "rgba(10,10,10,0.03)",
  /**
   * The ground under a row nobody has accepted yet — gold at 8%.
   *
   * A TINT rather than a badge, and this is the reason: the daily log's rows
   * are a table, read down a column at a time, and a chip on the two rows
   * that need attention out-shouts the numbers the table exists to show. The
   * tint is legible at a glance across a whole day and disappears the moment
   * the row is accepted (design: "Led AI Console Redesign", 8a).
   */
  pendingTint: "rgba(247,197,43,0.08)",
  /** The header chip that carries a JUDGEMENT rather than a fact — priority,
   *  or a record still awaiting review. Gold at 28%, which is the design's
   *  one tinted chip: strong enough to pick out of a row of neutral ones,
   *  quiet enough that three chips still read as a row (design: "Led AI
   *  Console Redesign", 17a). */
  goldChip: "rgba(247,197,43,0.28)",
  /** The ground under the row a side panel is currently showing — ink at
   *  3.5%, paired with a 2px inset ink rule down its leading edge. Quieter
   *  than the pending tint on purpose: it marks WHERE YOU ARE, not what needs
   *  doing (design: "Led AI Console Redesign", 8a). */
  rowSelected: "rgba(10,10,10,0.035)",
  /**
   * The priority meter's bars — three of them, lit from the left.
   *
   * `meterOn` is the unlit bar on a LOW row, which still has to read as a bar
   * rather than as nothing; `meterOff` is the unlit bar everywhere else. Two
   * values rather than one because the design lights low's first bar at 30%
   * and leaves the other two at 12%, which is what makes "low" distinct from
   * "none recorded" at a glance (design: "Led AI Console Redesign", 14a).
   */
  meterOn: "rgba(10,10,10,0.3)",
  meterOff: "rgba(10,10,10,0.12)",
  /** The rule between rows of a DENSE table — lighter again than `hairRow`,
   *  which starts to read as a grid at 44px row heights. */
  hairDense: "rgba(10,10,10,0.045)",
  /**
   * The daily log's week strip (design: "Led AI Console Redesign", 11a/10a).
   *
   * Neutral ink alphas rather than the console's warm `sub`/`sub2`, matching
   * the rest of the redesign's card interiors (`hairRow`, `rowSelected`): the
   * strip sits on white, where a warm grey reads as brown.
   *
   * Two golds, and the difference is the point. `weekPending` marks a day
   * that still has entries waiting on you; `weekSelected` marks the day you
   * are looking at. A selected day that is ALSO pending takes the stronger
   * one, so "where I am" never disappears under "what needs doing".
   */
  weekLabel: "rgba(10,10,10,0.38)",
  weekCount: "rgba(10,10,10,0.52)",
  /** The selected day's weekday and count — a step darker, so the tinted
   *  cell's own text reads as foreground rather than as more chrome. */
  weekOn: "rgba(10,10,10,0.72)",
  weekCountOn: "rgba(10,10,10,0.62)",
  /** A day number with nothing logged on it, and the dot that stands in for
   *  the count line it therefore has none of. */
  weekDim: "rgba(10,10,10,0.30)",
  weekDot: "rgba(10,10,10,0.12)",
  weekPending: "rgba(247,197,43,0.10)",
  weekSelected: "rgba(247,197,43,0.16)",
  /**
   * The properties column and its pickers (design: "Led AI Console
   * Redesign", 13a–13f).
   *
   * Turn 13's whole argument is that a property should not look like a form
   * field: "Properties are a quiet label → value column; the value is the
   * control." That only works if the resting state is nearly invisible, which
   * is what this ramp is for — a label at 45%, its value at 84%, an unset one
   * at 40%, and a ground under the value ONLY while it is being edited.
   *
   * Neutral ink alphas, like the rest of the redesign's card interiors
   * (`hairRow`, `rowSelected`, the week strip): these sit on white, where the
   * console's warm `sub` reads as brown.
   */
  propLabel: "rgba(10,10,10,0.45)",
  propValue: "rgba(10,10,10,0.84)",
  /** An unset value ("Add location"), and a picker's placeholder. Deliberately
   *  LIGHTER than the label beside it: an empty value is an invitation, not a
   *  fact, and at the label's own weight it read as one. */
  propEmpty: "rgba(10,10,10,0.40)",
  /** The ground under the value being edited — the only chrome a property
   *  ever grows, and only while the picker is open. */
  propOpen: "rgba(10,10,10,0.06)",
  /** The × that clears a set property, shown on hover — a filled grey disc
   *  with the surface's own colour for the cross, as Linear draws it. */
  propClear: "rgba(10,10,10,0.28)",
  /** shadcn's `ring-ring/50` for a focused text input — a 3px halo in this
   *  console's ink rather than a blue outline. */
  inputRing: "rgba(10,10,10,0.10)",
  /** The picker's edge and its lift. Two shadow layers, near and far, as
   *  everywhere else in the redesign. */
  popLine: "rgba(10,10,10,0.09)",
  popShadow: "rgba(10,10,10,0.14)",
  /** An option's number key — quieter than the option it selects, because it
   *  is a shortcut hint and not part of the choice. */
  popNum: "rgba(10,10,10,0.35)",
  /** A day the month grid shows but does not own. */
  calOutside: "rgba(10,10,10,0.22)",
  /** A choice with no colour of its own — "Not started", "Low", and anything
   *  a future schema adds. A picker where some rows have a dot and some do
   *  not reads as a rendering fault, so every row gets one. */
  propNeutralDot: "rgba(10,10,10,0.30)",
  /**
   * Home's project tiles (design: "Led AI Console Redesign", 16b).
   *
   * The third set of neutral ink alphas in this file, after the week strip's
   * and the properties column's. They are separate because the redesign gives
   * each surface its own ramp and a shared one would have to be re-checked
   * against three artboards every time any of them moved — but if a fourth
   * arrives, that is the moment to fold all four into one scale.
   */
  tileMeta: "rgba(10,10,10,0.52)",
  /** The mono line naming where a project syncs — quieter than the status
   *  under it, because it is a fact about the project rather than about the
   *  work waiting on it. */
  tileSource: "rgba(10,10,10,0.38)",
  /** The ground behind the All-projects glyph. */
  tileGlyph: "rgba(10,10,10,0.06)",
  /** The SELECTED tile is ink-filled, so its own quiet text is white at the
   *  same weight the others are ink at. */
  tileOnMeta: "rgba(255,255,255,0.62)",
  /** ...and it lifts, which is what makes "this is the one you picked" read
   *  before any of the words do. */
  tileShadow: "rgba(10,10,10,0.18)",
  /**
   * The Item list's filter cards (design: "Led AI Console", ISSUES screen).
   *
   * NOTE, for whoever adds the fifth: this is the fourth set of neutral ink
   * alphas in this file — after the week strip's, the properties column's and
   * the project tiles' — and they now overlap in value while differing in
   * name. `weekLabel`, `tileSource` and `filterLabel` are all 38%. Each was
   * added because the design gives each surface its own ramp and a shared one
   * would have to be re-checked against four artboards on every move, but the
   * cost has caught up with the reason. Fold them into one scale rather than
   * adding a fifth.
   */
  filterLabel: "rgba(10,10,10,0.38)",
  /** The far layer under an open filter menu. Deeper than a card's lift and
   *  shallower than `popShadow`, because this menu is wide and a heavy shadow
   *  under a 360px panel reads as a second surface rather than a raised one. */
  menuShadow: "rgba(10,10,10,0.12)",
  /** The redesign's page ground: a half-step warmer than `surface`, so the
   *  white cards on it read as raised rather than as the page itself. */
  pageGround: "#F7F3EB",
  /** Heavier hairline the design uses on interactive chrome (chips, buttons). */
  line2: "rgba(20,17,12,0.18)",
  /** The page ground behind the app frame (spec §6.2). */
  canvas: "#FBF4E8",
  /** The dim behind a modal sheet, verbatim from the design's Nudge modal
   *  (`rgba(20,17,12,0.34)`). Deliberately heavier than `line2` — a scrim
   *  that reads as a hairline does not separate the sheet from the page. */
  scrim: "rgba(20,17,12,0.34)",
  /** `surface` at zero alpha — the top stop of the Ask composer's fade
   *  (the settled design's `.ask` gradient). A literal `transparent` is
   *  rgba(0,0,0,0), which some engines interpolate through grey; this is the
   *  same RGB as `surface`, so the fade never greys. */
  surfaceFade: "rgba(255,253,248,0)",

  /*
   * Photo selection — the mode the gallery enters to act on several photos
   * at once ("Led AI Console.dc.html", PHOTOS screen).
   *
   * The floating action bar is the console's SECOND dark surface, after the
   * photo viewer, and like the viewer it needs white alphas of its own: text
   * on a near-black bar cannot borrow `surface`, which is a warm paper white
   * and reads as yellow there.
   */
  /** Text on the action bar. Pure white, not `surface` — see above. */
  selBarOn: "#FFFFFF",
  /** The hairline between the count and the select-all link. */
  selBarRule: "rgba(255,255,255,0.15)",
  /** The select-all link: present, but quieter than the count beside it. */
  selBarQuiet: "rgba(255,255,255,0.72)",
  /** The secondary action's outline — an edge on ink, not a fill. */
  selBarEdge: "rgba(255,255,255,0.18)",
  /** The bar floats over the gallery and has to read as floating. */
  selBarShadow: "rgba(10,10,10,0.30)",
  /** A day header whose photos are ALL picked. Gold at 16% is the same value
   *  as `weekSelected` — see the note further up about this file's
   *  overlapping neutral and gold alphas. Kept separate because one marks the
   *  day a reader chose in a strip and this marks a day fully selected, and
   *  the two would be edited for different reasons. */
  selGold: "rgba(247,197,43,0.16)",
  /** ...and its edge, gold at 50%. */
  selGoldEdge: "rgba(247,197,43,0.50)",
  /** The day control's label. Gold-free ink at 72% — the same value as
   *  `weekOn`, and kept apart for the same reason as `selGold` above. */
  selLabel: "rgba(10,10,10,0.72)",
  /*
   * Ask (design: "Led AI Console", the ASK screen).
   *
   * The composer there is a CARD, not the pill the rest of the console uses,
   * and it carries a second, wider shadow so it reads as the one thing on an
   * otherwise empty page. `askLift` is that shadow's colour; the console's
   * `softShadowLow` is 3% and too faint to lift a 20px-radius card off the
   * paper ground.
   */
  askLift: "rgba(10,10,10,0.06)",
  /** The reader's own question in a thread — a tinted bubble, where the
   *  answer beside it is bare text. Same ink alpha as `askLift` and a
   *  different thing: one is a shadow, one is a fill. */
  askSaid: "rgba(10,10,10,0.06)",
  /** A suggestion card's edge. Lighter than `line`, because four of them sit
   *  in a grid and `line` at that count reads as a table. */
  askCardEdge: "rgba(10,10,10,0.075)",
  /** The tick box on a tile. It sits ON a photograph, so unchecked is a
   *  near-opaque white chip rather than an outline — an outline disappears
   *  against a pale frame, which is most of them on a job site. */
  selBoxBg: "rgba(255,255,255,0.85)",
  selBoxEdge: "rgba(10,10,10,0.25)",
  selBoxShadow: "rgba(10,10,10,0.20)",

  /*
   * SIGN IN — the brand panel (design: "Led AI - Console Sign In", 1a).
   *
   * The console's THIRD dark surface, after the photo viewer and the photo
   * selection bar, and the only one a signed-out visitor ever sees. It gets
   * its own white alphas for the same reason `selBar*` does: `surface` is a
   * warm paper white and reads as yellow on ink, and `sub`/`sub2` were mixed
   * for a paper card and go brown here.
   *
   * Kept apart from `selBar*` because these are a different surface at a
   * different scale — a half-screen panel carrying 44px display type, not a
   * floating bar of 13px labels — and the two would be edited for different
   * reasons.
   */
  /** The panel's headline. Pure white; see above. */
  signInOn: "#FFFFFF",
  /** The sentence under the headline — present, clearly secondary. */
  signInOnSoft: "rgba(255,255,255,0.62)",
  /** The panel's mono labels: the wordmark's "CONSOLE", the readout, the
   *  partner caption. A step brighter than `signInOnSoft` because mono at
   *  10px with 1.8px tracking loses more to the ground than a 16px sentence
   *  does. */
  signInOnMeta: "rgba(255,255,255,0.70)",
  /** The live-readout pill: a lifted patch of the panel, not a card. 6% is
   *  the design's own value and is deliberately below the 8% edge that
   *  outlines it — the fill reads as light falling on the panel, the edge
   *  as the pill's own shape. */
  signInChip: "rgba(255,255,255,0.06)",
  signInChipEdge: "rgba(255,255,255,0.08)",
  /** The tile a partner mark sits on. The repo's Procore artwork is black on
   *  transparent (see `assets/procoreMark.ts`), so on ink it needs a ground
   *  of its own or it disappears; near-white rather than white so it sits in
   *  the panel rather than on top of it. */
  signInTile: "rgba(255,255,255,0.92)",
  /** A perk's tick disc on the sign-up panel — gold at 14%, the artboard's
   *  own value. The tick itself is `gold`; this is the light behind it. */
  signInTick: "rgba(244,190,46,0.14)",
  /** A perk's label. Brighter than `signInOnSoft`, which is the panel's
   *  prose: these three lines are the offer, and read as a list rather than
   *  as a paragraph. */
  signInOnStrong: "rgba(255,255,255,0.85)",
  /** The sign-in form's quiet CONTROL ink — "Use passkey instead".
   *  The design's own value, and deliberately neither of the two tones
   *  beside it: `sub` is the subtitle's grey and makes a control look like
   *  prose, `ink` is the CTA's and makes a second primary action out of an
   *  alternative one. */
  signInQuiet: "#3F3B35",
  /** The gold bloom in the panel's bottom-right corner, and its outer stop.
   *  A radial that has to fade to the SAME hue at zero alpha — a literal
   *  `transparent` is rgba(0,0,0,0) and some engines interpolate it through
   *  grey, which puts a dirty ring around the glow. Same trick as
   *  `surfaceFade`. */
  signInGlow: "rgba(244,190,46,0.28)",
  signInGlowFade: "rgba(244,190,46,0)",
} as const;

/**
 * The console shell + Home redesign (LED-1151, `home-projects.dc.html`).
 *
 * TWO-TONE grounds, always: the nav is one step darker than the main pane,
 * and scoping into one project warms BOTH by one step. These are the design's
 * four literals, verbatim — the shell and Home read them from here and
 * nowhere else.
 */
export const H = {
  /** Nav / main ground with "All projects" in force. */
  navAll: "#F3EDE2",
  mainAll: "#FAF7F1",
  /** Nav / main ground once a project is scoped — one step warmer each. */
  navScoped: "#EFE7D9",
  mainScoped: "#F7F2E8",
  /** Pure white cards, controls and popovers on those grounds. */
  white: "#FFFFFF",
  ink: "#14110C",
  /** The design's three warm greys, darkest first. */
  text: "#3F3B35",
  sub: "#6E6357",
  muted: "#8C8275",
  faint: "#9C9180",
  dim: "#B4AFA5",
  /** Every card and popover edge — 8% ink — and the hover edge. */
  edge: "rgba(20,17,12,0.08)",
  edgeStrong: "rgba(20,17,12,0.12)",
  edgeHover: "rgba(20,17,12,0.25)",
  rowHover: "rgba(20,17,12,0.05)",
  /** Picker rows: hover / keyboard highlight / the chosen project. */
  pickHover: "#F7F1E6",
  pickHi: "#F3EBDB",
  pickFoot: "#FBF8F2",
  /** A project's resting dot, and the gold dot of a scoped one. */
  dotIdle: "#C9C1B1",
  scopeDot: "#F7C52B",
  /** The one gold primary action per surface. */
  gold: "#F7C52B",
  goldInk: "#B08A1E",
  /** Status. Green = done / clear / live; amber = waiting; red = overdue. */
  green: "#00A368",
  greenSoft: "#E4F6EE",
  greenInk: "#04613D",
  greenSub: "#3F7A5E",
  amberDot: "#D9A514",
  amberInk: "#8A6400",
  amberSoft: "#FBF1D8",
  red: "#D94B3B",
  redInk: "#B5382A",
  redSoft: "#FBE3DC",
  /** The punch bars' track and the widgets' small icon wells. */
  track: "#F3EEE4",
  /** Observations' record-type colours (amber on cream), as everywhere else. */
  obsWell: "#F8EDD2",
  obsInk: "#C48A00",
  /** MapThumb: land, blocks, streets, and the strip behind it. */
  mapLand: "#E7E2D6",
  mapBlock: "#DED7C7",
  mapStreet: "#F5F1E8",
  mapStrip: "#F5F0E7",
  /** The project tiles' faint inner rule under the map strip. */
  hairFaint: "rgba(20,17,12,0.06)",
  /** Dark "All projects" tile: its icon well and its sub-line. */
  onInkWell: "rgba(255,255,255,0.12)",
  onInkSub: "rgba(255,255,255,0.6)",
  /** The tiny ring-pin's drop shadow. */
  pinShadow: "rgba(0,0,0,0.3)",
  /** Popover and pill shadows. */
  popShadow: "rgba(20,17,12,0.2)",
  pillShadow: "rgba(20,17,12,0.06)",
  focusRing: "rgba(20,17,12,0.08)",
  /** Avatar fills for the share list and the header stack. */
  avatars: ["#14110C", "#2E7F5E", "#2E7F7F", "#BC5475", "#6E4FA8", "#8E6232", "#3A7CA5"] as readonly string[],
  /** Walk-card photo placeholders (the design system's PhotoTile tones). */
  tiles: [
    ["#DCD1BF", "#A89680"],
    ["#CFA676", "#8B6B44"],
    ["#C9A88F", "#A67A5E"],
    ["#C7C2BD", "#9B968F"],
    ["#B8A891", "#8A7960"],
    ["#BCCAD2", "#7E9BAB"],
  ] as readonly (readonly [string, string])[],
} as const;

/**
 * Typography. The design pairs a text face with JetBrains Mono for the
 * small-caps meta lines that run throughout it (spec §6.2). Both are loaded
 * from Google Fonts in `index.html`, exactly as `backend/`'s own SPA shell
 * does, and both carry a real fallback stack so the console still reads
 * correctly if the font request fails.
 */
export const FONT_TEXT =
  "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Helvetica, Arial, sans-serif";
/**
 * The Led AI design system's own values (`_ds/led-ai-design-system-f7c932…/
 * tokens/colors.css`, plus the handful of greys the Team artboard
 * `Led AI - Team Web.dc.html` writes inline). The Team page is drawn from
 * that artboard and uses THESE, not `C`: the design system's ink is a true
 * near-black (`#0A0A0A`) where the console's is warm, its gold is a shade
 * brighter, and its greens differ — mixing the two on one screen would show.
 * Kept here, not in the view, so the hex guard still has one home.
 */
export const DS = {
  paper: "#F5F0E7",
  surface: "#FFFFFF",
  surfaceSunk: "#F1F1EE",
  ink: "#0A0A0A",
  ink2: "#6F6A60",
  ink3: "#A8A296",
  /** The artboard's label grey — eyebrows, mono meta, column heads. */
  meta: "#8C877D",
  /** Secondary body text on the artboard ("Walks and records on…"). */
  body2: "#6B665D",
  /** A zero, a dash, a seat with nothing: the faintest ink that still reads. */
  faint: "#C4BFB5",
  /** The dashed ring around a person not on Led AI yet. */
  dashed: "#B4AFA5",
  /** Row hover and the footer strip of the Procore card. */
  rowHover: "#FCFAF6",
  /** The sparkline cell with nothing to draw. */
  sparkEmpty: "#F7F4EE",
  /** "Edit people" at rest. */
  link: "#4A463F",
  /** The people picker (`Led AI - Pilot People Picker.dc.html`): a project
   *  row under the pointer, a directory row under the pointer, a checked
   *  row, the "+N" overflow pip, and a ghost button under the pointer. */
  pickerProjectHover: "#F3EEE5",
  pickerRowHover: "#FBF8F2",
  pickerRowOn: "#FFFBEF",
  pickerOverflow: "#EFE9DE",
  ghostHover: "#F1ECE2",
  /** Avatar grounds for the picker's chips, by person, darkest first. */
  avatars: ["#14110C", "#4A463F", "#6B665D", "#8C877D"],
  line: "rgba(10,10,10,0.07)",
  line2: "rgba(10,10,10,0.13)",
  gold: "#F7C52B",
  goldInk: "#4A3A00",
  /** The accent as text (the Ask bolt). Same value the console's `goldInk` carries. */
  accentInk: "#B08A1E",
  green: "#00A368",
  greenSoft: "#E2F3EA",
  amber: "#B07A00",
  amberSoft: "#F6ECCF",
  red: "#D94B3B",
  redSoft: "#FBE3DC",
  /** The pilot chart's bands: baseline grey, checkpoint gold, the open checkpoint paler. */
  bandBase: "#F1EEE8",
  bandCp: "#FBF3DC",
  bandCpOpen: "#FDF8EA",
  /** Checkpoint labels over their gold bands. */
  bandCpInk: "#8A6D00",
  /** The record types' stacked bars and the photos bar (`Led AI - Records vs Photos Chart.dc.html`). */
  typeDailyLog: "#0077C4",
  typeObservation: "#C48A00",
  typeTask: "#0A0A0A",
  typePunch: "#D94B3B",
  photosBar: "#4A463F",
  shadowFab: "0 10px 24px rgba(10,10,10,0.25)",
  shadowCard: "0 1px 2px rgba(10,10,10,0.04)",
  /* The Pilot Schedule modal (`Led AI - Pilot Schedule Modal.dc.html`). */
  /** A stepper card's ground. */
  stepperBg: "#F8F5EF",
  /** The inactive step pills in the modal's progress strip. */
  progressOff: "#E4DED3",
  /** The schedule bar's segments: baseline, ramp-up hatch (two tones), checkpoint, after-last-checkpoint. */
  segBase: "#ECE7DD",
  segRampA: "#F4EFE6",
  segRampB: "#ECE6DA",
  segCp: "#FBEFC6",
  segTail: "#F4F1EB",
  /** The ramp/tail legend dots — and the · between the upload card's two
   *  footer lines (`views/ProjectDetails.tsx`), which is the same value. */
  segDot: "#D9D2C4",
  /*
   * Project Details — Directory and Locations — and the Integrations drawer
   * (`Led AI - Project Details.dc.html`; `views/ProjectDetails.tsx`,
   * `components/IntegrationsDrawer.tsx`, and the `.pd-*` hover rules in
   * `main.tsx`). The artboard's own values, verbatim. Where one also lives in
   * another palette the twin is named, so nobody re-derives it; it is kept
   * here rather than borrowed because the page draws in THIS palette and the
   * console's `C` is not on it (see the view's header).
   */
  /** Body copy a step under `ink` — a person's role, the upload guide's
   *  steps, the help card's prose. Same value as the console's `C.signInQuiet`. */
  inkMuted: "#3F3B35",
  /** Mono meta a step quieter than `meta` — a row's source tag, OPTIONAL
   *  beside a column, the drop zone's hint. Same value as the console's `C.sub2`. */
  metaQuiet: "#9C9180",
  /** A word that warns: "No role", "No company", REQUIRED, and the note that
   *  a provider is rate-limiting and the page is showing its last copy. */
  warnInk: "#B26A00",
  /** The dot beside a warning — a picker option that warns, the no-company notice. */
  warnDot: "#D98A0B",
  /** The no-company notice: its ground, and the sentence set on it. The
   *  ground is the same value as the record page's `RD.syncWaitSoft`. */
  warnSoft: "#FBF1D8",
  warnDeep: "#5C4A12",
  /** The project's sync line once it has a source. Same value as the record
   *  page's `RD.syncOk` and the daily report's `ADR.green`. */
  syncInk: "#04613D",
  /** An alert's own words on `redSoft` — a file the server rejected, a
   *  sign-in that failed. Deeper than `RD.redInk` / `ADR.redInk` (#B5382A). */
  alertInk: "#7A2418",
  /** The depth chip's ground ("L2"), and a progress bar's track. */
  chip: "#F3EEE3",
  /** A table's head row, its load-more footer, and a code sample: a step
   *  warmer than the card. */
  tableHead: "#FAF7F1",
  /** The card's recess — a footer, the drop zone, the picked file, the help
   *  row — and the ground a directory row lifts to under the pointer
   *  (`.pd-row:hover`). One step lighter than `rowHover`, which is the Team
   *  artboard's; the two artboards differ by that step. */
  soft: "#FDFBF6",
  /** A location-tree row under the pointer or keyboard (`.pd-tree-row:hover`):
   *  a pale gold, where the directory's row lifts to `soft`. */
  treeHover: "#FDF6E1",
  /** The bar beside a branch, filled to the share of locations it holds. */
  treeBar: "#D9B23C",
  /** The gold button's two stops, top then bottom — and, the bottom stop on
   *  its own, the depth chip once its row is lit. */
  goldLight: "#FDCC35",
  goldDeep: "#F6BE18",
  /** The tile a picked CSV sits on. A deeper green than `green`: it is a
   *  file mark, not a status. */
  fileTile: "#00875A",
} as const;

export const FONT_MONO =
  "'JetBrains Mono', ui-monospace, SFMono-Regular, Menlo, Consolas, monospace";

/**
 * The design's project-tone palette, verbatim (`const TONES` in its script).
 * Muted, deliberately low-chroma so a sidebar of six of them reads as one
 * family rather than as six status colours — which is why the semantic
 * red/green/amber above are NOT in this list.
 */
export const PROJECT_TONES: readonly string[] = [
  "#8AA0B0",
  "#B08A6A",
  "#8A9678",
  "#9A96A8",
  "#7E96A6",
  "#A8A296",
  "#B0A08A",
  "#96A8B0",
];

/**
 * 32-bit FNV-1a over the id's UTF-16 code units.
 *
 * Chosen because it is fully specified by arithmetic — no library, no
 * platform hash, no iteration order — so the same id yields the same number
 * in this browser, in a Bun test, and in any future process that needs the
 * same dot. `Math.imul` keeps the multiply in 32-bit space (a plain `*`
 * loses precision past 2^53 and would make the result depend on how far
 * along the string you are); `>>> 0` keeps it unsigned.
 */
function fnv1a32(value: string): number {
  let hash = 0x811c9dc5;
  for (let i = 0; i < value.length; i++) {
    hash ^= value.charCodeAt(i);
    hash = Math.imul(hash, 0x01000193) >>> 0;
  }
  return hash >>> 0;
}

/**
 * The colour dot for a project, DERIVED from its job-site id rather than
 * stored on the document (spec §6.2). Deterministic and total: the same id
 * always yields the same tone — across reloads, across processes, across
 * machines — and every string yields one, including the empty string.
 *
 * Derived rather than stored on purpose: a stored field would need a backend
 * write path, a migration for every existing site, and would then be a second
 * thing that can disagree with the id it decorates.
 */
export function projectDot(jobSiteId: string): string {
  return PROJECT_TONES[fnv1a32(jobSiteId) % PROJECT_TONES.length]!;
}

/**
 * The hatch the design lays over every evidence tile at 22% opacity, so an
 * un-loaded photo reads as a placeholder rather than as a coloured block.
 * Lives here because it carries the one colour in the console that is not a
 * palette entry — plain white — and this file is the only place a colour is
 * allowed to be written down.
 */
export const TILE_HATCH = "repeating-linear-gradient(45deg,#fff 0 1px,transparent 1px 7px)";

/**
 * The 44px evidence tile the design draws behind each row, in that project's
 * tone. A gradient, not a flat fill, matching the design's
 * `linear-gradient(135deg, <tone>, <tone>99)`.
 */
export function projectTile(jobSiteId: string): string {
  const tone = projectDot(jobSiteId);
  return `linear-gradient(135deg,${tone},${tone}99)`;
}

// Preserve the annotation tools and walk-review design palettes in the shared source.
export const ANNOTATION_COLORS = {
  white: "#FFFFFF",
  black: "#101010",
  red: "#E94F37",
  green: "#16A34A",
  blue: "#0EA5E9",
  yellow: "#F59E0B",
} as const;

export const ANNOTATION_CHROME = {
  border: "#555",
  button: "#252525",
  background: "#111",
  onBackground: "#fff",
  save: "#edc44a",
  swatchBorder: "#777",
  hint: "#aaa",
  error: "#ffb2a7",
} as const;

export const WALK_PHOTO_COLORS = {
  ink: "#0A0A0A",
  body: "#14110C",
  sub: "#6B665D",
  sub2: "#8C877D",
  faint: "#A8A296",
  ghost: "#B4AFA5",
  hover: "#F6F1EA",
  green: "#00A368",
  red: "#B5382A",
  observation: "#C48A00",
  observationBg: "#F8EDD2",
  punch: "#D94B3B",
  punchBg: "#FBE4E0",
  neutralBg: "#EFE9DE",
  photo: "#4A463F",
  log: "#0077C4",
  logBg: "#E0EEF8",
  surface: "#fff",
  footer: "#FCFAF6",
  sendIcon: "#F7C52B",
  link: "#B08A1E",
  placeholder: "#ECE7DC",
  deleteHover: "#FBEDEA",
} as const;

/**
 * The record page's palette — Claude Design "Record Detail Web v2"
 * (`views/RecordDetail.tsx`), which draws in the phone's own values rather
 * than the console's `C`. Kept here so this file stays the one place a colour
 * is written down.
 */
export const RD = {
  ink: "#14110C",
  sub: "#6E6357",
  ter: "#8C8275",
  quiet: "#9C9180",
  card: "#FFFDF8",
  bg: "#FBF4E8",
  sec: "#F1E9D8",
  sunk: "#F3ECDB",
  green: "#00B877",
  /** A selected location row — the phone's `--signal-green-soft`. */
  greenSoft: "#E4F6EE",
  /** The same row under the pointer or keyboard. */
  greenSoftActive: "#D3EFE2",
  gold: "#F4BE2E",
  red: "#D94B3B",
  /** Red words on the card: a value that will not save. */
  redInk: "#B5382A",
  info: "#0077C4",
  amber: "#C48A00",
  orange: "#C56824",
  violet: "#8055B5",
  rose: "#BC5475",
  teal: "#16847E",
  /** The tick a copy button turns into. */
  copied: "#00A368",
  /** A selected popover row under the pointer or keyboard. */
  selActive: "#E6DCC6",
  /** An unselected popover row under the pointer or keyboard. */
  rowActive: "#F7F1E4",
  /** The › and · between crumb parts. */
  crumbRule: "#C4BFB5",
  /** Behind a photo while it loads. */
  photoGround: "#E9E2D6",
  /** Text and icons on a dark photo badge. */
  white: "#FFFFFF",
  /** Led AI's sparkle in the activity list. */
  ledMark: "#4A463F",
  /** Procore sync — the sidebar row, its hover card and the activity dot:
   *  ink for the words, soft for the disc behind the icon. */
  syncOk: "#04613D",
  syncOkSoft: "#E4F6EE",
  syncWait: "#8A6400",
  syncWaitSoft: "#FBF1D8",
  syncBad: "#9A2E22",
  syncBadSoft: "#FBE3DC",
};

/**
 * The Autodesk daily report's palette — Claude Design "Led AI - Daily Report
 * Editor" (`views/AutodeskDailyReport.tsx`, `views/AutodeskReviewParts.tsx`),
 * which, like the record page, draws in the phone's own values.
 */
export const ADR = {
  label: "#8C8275",
  card: "#FFFFFF",
  paper: "#FFFDF8",
  hover: "#F7F1E6",
  recess: "#FBF8F2",
  menuOn: "#F1E9D8",
  tile: "#E9E2D6",
  track: "#EFE8DA",
  dot: "#C4BFB5",
  faintRule: "#B4AFA5",
  green: "#04613D",
  greenMark: "#00A368",
  greenSoft: "#DFF3E7",
  blue: "#0077C4",
  blueInk: "#005E9C",
  blueSoft: "#E0EEF8",
  red: "#D94B3B",
  redInk: "#B5382A",
  redSoft: "#FBE4E0",
  amberInk: "#8A5A00",
  /** The review card's field tray ("Daily Report Editor", review screen). */
  tray: "#F3EDE1",
  /** A record set aside with Later. */
  later: "#8A6400",
  /** The phone's `--avatar-tile`: the ground behind a person's initials. */
  avatarTile: "#EFE7D8",
};

/**
 * The records table — Observations, Punch and Autodesk Issues
 * (`Led AI - Records Table.dc.html`). Its chrome is the design system's
 * (`DS`); these are the values that artboard adds on top: the table's warm
 * header strip, the row and pill hovers, the punch workflow's own status
 * colours, and the avatar / company swatches it paints people and subs with.
 */
export const RT = {
  pane: "#FFFFFF",
  /** Header row, footer row and the list's row hover. */
  strip: "#FAF7F1",
  /** A table row under the pointer. */
  rowHover: "#FBF9F4",
  /** Any small control under the pointer; the chosen option in a menu. */
  hover: "#F7F3EB",
  /** A selected view pill, an open Filter button. */
  pillOn: "#F3EEE3",
  /** The view pills' mask below RECORDS_COMPACT_QUERY, where they scroll:
   *  solid to 24px short of the right edge, then gone, so a cut-off pill says
   *  there is more. A mask reads alpha only, so the `transparent` that greys
   *  a painted fade (see `C.surfaceFade`) is safe here. */
  viewsFade: "linear-gradient(to right, #000 calc(100% - 24px), transparent)",
  /** The project pill in the sidebar and the modal's tag. */
  projectPill: "#FDFBF6",
  /** Cell text one step below ink. */
  cell: "#3F3B35",
  /** Mono counts beside a label. */
  count: "#9C9180",
  /** The list band's ground, under its status-tinted wash. */
  band: "#F5F1EA",
  /** A photo tile before it has loaded. */
  photoBg: "#E9E2D6",
  /** The priority meter's unlit bars, and its unrated ones. */
  barOff: "#E4DDCF",
  barNone: "#C9C1B1",
  /** A toggle switched off. */
  toggleOff: "#D9D2C4",
  /** The review count's pill in the nav. */
  badge: "#FBEFC6",
  /** A due date that has passed. */
  overdue: "#B5382A",
  /** The toast's check, on ink. */
  greenBright: "#00D38A",
  /** Workflow status colours (Procore punch, observations, ACC issues). */
  status: {
    draft: "#B4AFA5",
    initiated: "#3A7CA5",
    working: "#D98A0B",
    readyToClose: "#00875A",
    closed: "#00A368",
    rejected: "#D94B3B",
    dispute: "#8E6232",
  },
  /** Priority colours, high → low, and Low's quieter swatch in the picker. */
  priority: { high: "#D94B3B", medium: "#D98A0B", low: "#B4AFA5", lowSwatch: "#9C9180" },
  /** People: [ground, initials]. Picked deterministically per name. */
  avatars: [
    ["#0A0A0A", "#F7C52B"],
    ["#3A7CA5", "#FFFFFF"],
    ["#8E6232", "#FFFFFF"],
    ["#2E7F7F", "#FFFFFF"],
    ["#D98A0B", "#0A0A0A"],
    ["#00875A", "#FFFFFF"],
    ["#6B4FA0", "#FFFFFF"],
  ] as ReadonlyArray<readonly [string, string]>,
  /** Companies: [swatch, initials ink]. The last is the neutral swatch. */
  companies: [
    ["#F6BE18", "#0A0A0A"],
    ["#00875A", "#FFFFFF"],
    ["#3A7CA5", "#FFFFFF"],
    ["#8E6232", "#FFFFFF"],
    ["#2E7F7F", "#FFFFFF"],
    ["#C9C1B1", "#0A0A0A"],
  ] as ReadonlyArray<readonly [string, string]>,
  /** Ink for a company name drawn on the neutral swatch. */
  companyMuted: "#6B665D",
  /** The move dialog: a picked person, a required box still empty, the
   *  send glyph on the ink button, the footer's ground and separators. */
  pickOn: "#FBF3DC",
  pickOnLine: "rgba(176,138,30,0.5)",
  needed: "#F6BE18",
  sendGlyph: "#F7C52B",
  sheetFoot: "#FDFBF6",
  dotSep: "#C4BFB5",
  shadowMenu: "0 14px 34px rgba(10,10,10,0.14)",
  shadowPopover: "0 16px 36px rgba(10,10,10,0.16)",
  shadowModal: "0 30px 80px rgba(10,10,10,0.28)",
  shadowTip: "0 10px 24px rgba(10,10,10,0.22)",
  shadowToast: "0 14px 34px rgba(10,10,10,0.25)",
  scrim: "rgba(10,10,10,0.28)",
} as const;

/** `#3A7CA5` at alpha `a`, for washes built from a token. */
export function withAlpha(hex: string, a: number): string {
  const h = hex.replace("#", "");
  const n = parseInt(h.length === 3 ? h.split("").map((c) => c + c).join("") : h, 16);
  return `rgba(${(n >> 16) & 255},${(n >> 8) & 255},${n & 255},${a})`;
}

/**
 * The Review queue redesign (LED-1153/1155/1156, `home-projects.dc.html`,
 * "Review" and "Review card"): the open walk's gold tile and outline, a
 * finished walk's green tile, the manpower conflict panel, and the name
 * matching pill's warning treatment.
 */
export const RV = {
  /** The open walk's count tile and its row outline. */
  tileGold: "linear-gradient(180deg,#FDCC35,#F6BE18)",
  tileGoldSub: "#5A4500",
  rowGold: "#F6BE18",
  /** A walk with records waiting (not open). */
  tileRest: "#F1EAD9",
  /** A finished walk: nothing left. */
  tileDone: "#E4F6EE",
  tileDoneInk: "#04613D",
  /** The column's ground, the meta under a title, a row under the pointer. */
  queueGround: "#FBF8F1",
  meta: "#9C9180",
  rowHover: "#F7F1E6",
  /** The IN REVIEW badge. */
  badgeInk: "#8A6400",
  badgeBg: "#FBF1D8",
  /** Ink one step below the page's (a label beside its value). */
  ink2: "#3F3B35",
  /** Green: what goes to Procore, a finished walk, an accept. */
  green: "#00A368",
  greenInk: "#04613D",
  greenSoft: "#E4F6EE",
  /** The conflict panel's recess, its bubbles and their segmented control. */
  recess: "#F3EDE1",
  bubble: "#FFFFFF",
  bubblePill: "#FBF8F2",
  segTrack: "rgba(20,17,12,.07)",
  segShadow: "0 1px 3px rgba(20,17,12,.14)",
  outGround: "rgba(255,255,255,.5)",
  outLine: "rgba(20,17,12,.16)",
  outTag: "#B4AFA5",
  /** The name pill's "not in directory" warning: quiet amber. */
  warnBg: "#FDF6E4",
  warnLine: "rgba(196,138,0,.32)",
  warnInk: "#8A5A00",
  /** A candidate's face card. */
  faceBg: "#EFE7D8",
  faceRing: "#FFFFFF",
  rejectHover: "#FBE4E0",
};

/**
 * The Review queue's Walk photos card and the record a walk photo is made
 * into (Claude Design "Review · walk photos", LED-1154 —
 * `views/WalkPhotosPane.tsx`, `views/WalkPhotoOrigin.tsx`). The design's own
 * values; the Make it a glyphs and the ALREADY ON RECORDS tags are the
 * artboard's, which differ from the record-type pills elsewhere.
 */
export const WPC = {
  ink: "#14110C",
  black: "#0A0A0A",
  card: "#FFFDF8",
  white: "#FFFFFF",
  sub: "#6E6357",
  label: "#8C8275",
  quiet: "#9C9180",
  pill: "#F1EAD9",
  pillInk: "#3F3B35",
  tile: "#E9E2D6",
  tray: "#F3EDE1",
  hover: "#F7F1E6",
  green: "#00A368",
  greenInk: "#04613D",
  greenSoft: "#E4F6EE",
  red: "#D94B3B",
  redInk: "#B5382A",
  obsGlyph: "#2F6E99",
  logGlyph: "#0077C4",
  obsTag: "#8A6400",
  obsTagBg: "#F8EDD2",
  punchTagBg: "#FBE4E0",
  logTagBg: "#E0EEF8",
  taskTagBg: "#EFE9DE",
} as const;

/**
 * The Walk photos card's full-screen photo viewer (Claude Design "Led AI -
 * Web Photo Viewer" — `views/WalkPhotoViewer.tsx`) and its toast. The
 * design's own values; the record-type colours stay `WPC`'s.
 */
export const WPV = {
  gold: "#F7C52B",
  stage: "#111110",
  rail: "#1B1A18",
  thumb: "#2A2825",
  /** The leave-out / delete square's glyph. */
  out: "#E5624F",
  /** A problem line on the dark rail. */
  problem: "#F08C7D",
  /** The location dropdown. */
  menu: "#FFFDF8",
  menuSelected: "#F3EBDB",
  menuHover: "#F3EEE3",
} as const;

/**
 * The Daily log (LED-1152, Claude Design "Daily log" / "Daily log · detail" —
 * `views/DailyLog.tsx`, `views/DailyLogEntryEditor.tsx`,
 * `views/DailyLogPhotosPane.tsx`). The design's own values; most surfaces
 * reuse `ADR` / `RV` / `RD`, these are the ones only this page draws.
 */
export const DL = {
  ink: "#14110C",
  sub: "#6E6357",
  muted: "#8C8275",
  label: "#9C9180",
  ink2: "#3F3B35",
  card: "#FFFFFF",
  /** A row's TYPE tag and the Photos row's PHOTOS · N tag. */
  tagBg: "#F3EEE4",
  /** The open row, and a row under the pointer. */
  rowSelected: "#F3EBDB",
  rowHover: "#FBF8F2",
  /** The Manpower table's totals row. */
  totals: "#FBF8F2",
  /** The day on show in the week strip. */
  daySelected: "#FBF3DC",
  /** The gold CONFLICT tag. */
  conflictInk: "#8A6400",
  conflictBg: "#FBF1D8",
  photoGround: "#E9E2D6",
  /** The Group by walk switch. */
  switchOn: "#14110C",
  switchOff: "#D9D2C4",
  /** The kind pill (Daily log blue). */
  kindInk: "#0077C4",
  kindBg: "#E0EEF8",
  /** Activity marks. */
  okBg: "#E4F6EE",
  okInk: "#04613D",
  led: "#F7C52B",
  deleteHover: "#FBE3DC",
  deleteLine: "#D94B3B",
  failInk: "#B5382A",
  hairline: "rgba(20,17,12,.08)",
  rowLine: "rgba(20,17,12,.06)",
  control: "rgba(20,17,12,.12)",
  overlay: "rgba(10,10,10,.6)",
  shadowSwitch: "0 1px 3px rgba(20,17,12,.25)",
} as const;
